import { Fragment, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Icon } from "../../../components/Icon";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../../data";
import { useStaffWorkspace } from "../../../app/session";
import { talentName, useBrands, useDeals, useTalents } from "../../../data/queries";
import { EmptyState, ErrorState, Loading } from "../../../components/States";
import { KopfMehr, PageHead, PillView, SortTh, Toolbar, matchesSearch, useSorted, SortWahl } from "../../../components/ui";
import { date, today as todayIn } from "../../../lib/format";
import { loadVertragsListe } from "./daten";
import {
  contractNumberLabel, FILTER_LABEL, listKpis, matchesFilter, nextStep, openLinkExpiry, signerViews, statusPill, urgency, type ListFilter,
} from "./modell";
import { ExternerVertragDialog } from "./ExternerVertrag";
import { VertragVorschau, useVorschauParam } from "./VertragVorschau";
import { BeispielEtikett } from "../../../components/Beispiel";
import { Mehr } from "../../../components/ZeilenMenue";
import { chatLink } from "../../kommunikation/nachrichten/model";
import "./vertraege.css";
import { t, nameIn, sprachName, ta } from "../../../lib/sprache";
import { VoreinstellungenEintrag } from "../einstellungen/Voreinstellungen";
import { useNurLesen } from "../../../app/nurLesen";
import { PdfDesignLink } from "../../../components/PdfDesignLink";

/**
 * Verträge (Umfang des bisherigen Portals, Bausteine aus rechnungen.html): alle Verträge mit Stand je Unterzeichner,
 * dringende zuerst (abgelehnt, Link abgelaufen, Gegenzeichnung), Kennzahlen, Vorlagen und extern unterschriebene PDFs.
 */

const SHORT_STEP: Record<string, string> = {
  get edit() { return t("Bearbeiten", "Edit"); }, get send() { return t("Senden", "Send"); }, get resend() { return t("Neu senden", "Resend"); }, get declined() { return t("Neue Fassung", "New version"); }, get upload() { return t("PDF hochladen", "Upload PDF"); }, get countersign() { return t("Gegenzeichnen", "Countersign"); },
  get wait() { return t("Ansehen", "View"); }, get final() { return t("PDF verschicken", "Send PDF"); }, get none() { return t("Ansehen", "View"); },
};

/**
 * K-543 (Entscheidung Inhaber 20.10.2026): schlankes Zeilenmenü „⋯“, höchstens fünf Einträge je Stand; die Hauptaktion steht als
 * Knopf in der Zeile. Jede Aktion öffnet denselben Dialog wie auf der Detailseite (?aktion=…), mit derselben Rückfrage.
 */
function VertragMenue({ c, step }: { c: { id: string; status: string; talent_id: string | null; origin: string }; step: string }) {
  const weg = (aktion: string) => `/agentur/vertraege/${c.id}?aktion=${aktion}`;
  const punkte: { label: string; to: string }[] = [];
  if (c.status === "draft") {
    punkte.push({ label: t("Bearbeiten", "Edit"), to: `/agentur/vertraege/${c.id}` });
    if (c.origin !== "pdf_upload") punkte.push({ label: t("Senden", "Send"), to: `/agentur/vertraege/${c.id}/senden` });
  } else if (c.status === "sent" || c.status === "partially_signed") {
    if (step === "countersign") punkte.push({ label: t("Jetzt unterschreiben", "Sign now"), to: weg("gegenzeichnen") });
    // Nach einer Ablehnung hilft keine Erinnerung; dort führt der Schritt „Neue Fassung“ weiter
    if (step !== "declined") punkte.push({ label: t("Erinnern (neuen Link senden)", "Remind (send new link)"), to: weg("erinnern") });
    if (c.talent_id) punkte.push({ label: t("Im Chat teilen", "Share in chat"), to: chatLink(c.talent_id, { kind: "contract", id: c.id }) });
  } else if (c.status === "signed") {
    if (c.origin !== "pdf_upload") punkte.push({ label: t("Druckansicht und PDF", "Print view and PDF"), to: `/agentur/vertraege/${c.id}/druck` });
    if (c.origin !== "pdf_upload") punkte.push({ label: t("Unterschriftenprotokoll (PDF)", "Signature log (PDF)"), to: weg("protokoll") });
    if (c.talent_id) punkte.push({ label: t("Im Chat teilen", "Share in chat"), to: chatLink(c.talent_id, { kind: "contract", id: c.id }) });
  }
  if (c.status !== "draft" && c.origin !== "pdf_upload") punkte.push({ label: t("Als Kopie für einen neuen Vertrag", "As a copy for a new contract"), to: weg("kopie") });
  if (!punkte.length) return null;
  return (
    <Mehr>{(close) => (
      <>{punkte.slice(0, 5).map((x) => <Link key={x.label} role="menuitem" className="tk-inv-menuepunkt" to={x.to} onClick={close}>{x.label}</Link>)}</>
    )}</Mehr>
  );
}

const FILTERS: ListFilter[] = ["offen", "handeln", "entwuerfe", "unterschrift", "gegenzeichnen", "abgeschlossen", "aufgehoben", "alle"];

export function Vertraege() {
  const ws = useStaffWorkspace();
  // K-1/K-2: Pause und Nur-Lesen
  const nurLesen = useNurLesen();
  const navigate = useNavigate();
  const orgId = ws.org.org_id;
  const list = useQuery({ queryKey: ["contracts", orgId, "liste"], queryFn: () => loadVertragsListe(api, orgId) });
  const limits = useQuery({ queryKey: ["org_entitlements", orgId], queryFn: () => api.read<{ limits?: Record<string, number>; usage?: Record<string, number> }>("org_entitlements", { p_org: orgId }) });
  const talents = useTalents(orgId);
  const brands = useBrands(orgId);
  const deals = useDeals(orgId);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<ListFilter>("offen");
  const [kind, setKind] = useState("");
  const [talent, setTalent] = useState("");
  const [brand, setBrand] = useState("");
  // K-540: Gruppieren nach Creator bzw. Marke, Gruppen aufklappbar
  const [gruppe, setGruppe] = useState<"" | "creator" | "marke">("");
  const [zugeklappt, setZugeklappt] = useState<Set<string>>(() => new Set());
  const [external, setExternal] = useState(false);
  // K-512: Vorschau je Zeile, direkt aus der Liste (ohne Detailseite); offen über ?vorschau=<id>
  const vorschau = useVorschauParam();
  const now = Date.now();
  const today = todayIn(ws.org.timezone);
  const month = today.slice(0, 7);

  const rows = useMemo(() => {
    if (!list.data) return [];
    const t = new Map((talents.data ?? []).map((x) => [x.id, x]));
    const b = new Map((brands.data ?? []).map((x) => [x.id, x]));
    const d = new Map((deals.data ?? []).map((x) => [x.id, x]));
    const k = new Map(list.data.kinds.map((x) => [x.id, x]));
    return list.data.contracts.map((c) => {
      const views = signerViews(c, list.data.signers, now, list.data.portalTalents);
      const step = nextStep(c, views);
      return {
        c, views, step,
        rank: urgency(step, c),
        talent: c.talent_id ? talentName(t.get(c.talent_id)) : "–",
        brand: c.brand_id ? b.get(c.brand_id)?.name ?? "–" : "–",
        deal: c.deal_id ? d.get(c.deal_id)?.title ?? "" : "",
        kind: c.kind_id ? (k.get(c.kind_id) ? nameIn(k.get(c.kind_id)!.name_de, k.get(c.kind_id)!.name_en) : "") : "",
        expiry: openLinkExpiry(views),
      };
    }).sort((x, y) => x.rank - y.rank || (y.c.updated_at ?? "").localeCompare(x.c.updated_at ?? ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.data, talents.data, brands.data, deals.data]);

  const kpis = listKpis(rows, month);
  const counts = Object.fromEntries(FILTERS.map((f) => [f, rows.filter((r) => matchesFilter(f, r.c, r.step)).length])) as Record<ListFilter, number>;
  const shown = rows.filter((r) => matchesFilter(filter, r.c, r.step)
    && (!kind || r.c.kind_id === kind) && (!talent || r.c.talent_id === talent) && (!brand || r.c.brand_id === brand)
    && matchesSearch(`${contractNumberLabel(r.c.contract_number, r.c.contract_number_label)} ${r.c.title} ${r.talent} ${r.brand} ${r.deal} ${r.kind}`, search));
  const { sorted, sort, toggle } = useSorted(shown, {
    nummer: (r) => r.c.contract_number ?? 0,
    vertrag: (r) => r.c.title,
    // Studio (Creator-Konto): nur die Marke (es gibt nur das eigene Profil)
    creator: (r) => (ws.isCreator ? r.brand : `${r.talent} ${r.brand}`),
    status: (r) => r.rank,
    versendet: (r) => r.c.sent_at,
  });

  if (list.error) return <ErrorState error={list.error} retry={() => void list.refetch()} />;
  if (list.isPending || talents.isPending || brands.isPending || deals.isPending) return <Loading />;

  const maxPerMonth = limits.data?.limits?.["max_contracts_per_month"];
  const sentThisMonth = limits.data?.usage?.["contracts_this_month"] ?? kpis.sentThisMonth;

  const zeile = (r: (typeof sorted)[number]) => (
                <tr key={r.c.id} className="tk-vt-zeile" onClick={(e) => {
                  // S3 (D-1): die ganze Zeile öffnet den Vertrag; Knöpfe, Links und das Menü behalten ihre Wirkung
                  if ((e.target as HTMLElement).closest("a, button, input, label, select")) return;
                  navigate(`/agentur/vertraege/${r.c.id}`);
                }}>
                  <td className="num tk-vt-c-nr" data-label={t("Nr.", "No.")}>{contractNumberLabel(r.c.contract_number, r.c.contract_number_label)}</td>
                  <td className="tk-vt-kopf">
                    <strong title={r.c.title}><Link to={`/agentur/vertraege/${r.c.id}`}>{r.c.title}</Link><BeispielEtikett von={r.c} /></strong>
                    <small>{[r.kind, r.deal, r.c.origin === "pdf_upload" ? t("extern unterschrieben", "signed externally") : "", r.c.document_kind === "brand_pdf" ? t("PDF der Marke", "Brand's PDF") : "", r.c.amends_id ? t("Nachtrag", "Addendum") : "", r.c.language === "en" ? sprachName("en") : ""].filter(Boolean).join(" · ") || "–"}</small>
                    {/* D2-1: eingeklappte Spalten „Nr.“ und „Creator · Marke“ (schmale Liste) */}
                    <small className="tk-vt-alt">{[contractNumberLabel(r.c.contract_number, r.c.contract_number_label), ws.isCreator ? null : r.talent, r.brand].filter((x) => x && x !== "–").join(" · ")}</small>
                  </td>
                  <td data-label={ws.isCreator ? t("Marke", "Brand") : t("Parteien", "Parties")} className="tk-vt-c-parteien"><span>{[ws.isCreator ? null : r.talent, r.brand].filter((x) => x && x !== "–").join(" · ") || "–"}</span></td>
                  <td data-label="Status">
                    <PillView pill={statusPill(r.c, today)} />
                    {r.c.origin === "pdf_upload" ? <small>{t("PDF abgelegt", "PDF filed")}</small> : (
                      // S3: Unterzeichner als kleine Zeichen mit Tooltip; Abgelehnt oder Link abgelaufen farbig, für Vorleser ausgeschrieben
                      <small className="tk-vt-zeichen">{r.views.length ? r.views.map((v, i) => (
                        <Fragment key={`${v.role}-${v.slot}`}>{i > 0 ? " " : ""}<span className="tk-vt-sig" data-state={v.state} title={`${v.label}: ${v.pill.label}`}>{v.label}{"\u00A0"}{v.pill.symbol}
                          {(v.state === "declined" || v.state === "expired") && <span className="tk-vt-zeichen-text">: {v.pill.label}</span>}</span></Fragment>
                      )) : "–"}</small>
                    )}
                    {/* D2-1: eingeklappte Spalte „Versendet · Link bis“ (schmale Liste) */}
                    {r.c.origin !== "pdf_upload" && r.c.sent_at && <small className="tk-vt-alt">{t("Versendet ", "Sent ")}{date(r.c.sent_at, ws.org.timezone)}{r.expiry && r.c.status !== "signed" && r.c.status !== "void" && !r.c.declined_at ? t(` · Link bis ${date(r.expiry, ws.org.timezone)}`, ` · link until ${date(r.expiry, ws.org.timezone)}`) : ""}</small>}
                  </td>
                  <td data-label={t("Versendet", "Sent")} className="tk-vt-c-versendet">
                    <span>{r.c.origin === "pdf_upload" ? (r.c.external_signed_on ? date(r.c.external_signed_on, ws.org.timezone) : "–") : r.c.sent_at ? date(r.c.sent_at, ws.org.timezone) : "–"}</span>
                    {r.expiry && r.c.status !== "signed" && r.c.status !== "void" && !r.c.declined_at && <small>{t("Link bis ", "Link until ")}{date(r.expiry, ws.org.timezone)}</small>}
                    {r.c.status === "signed" && r.c.ends_on && <small>{r.c.auto_renew && !r.c.terminated_on ? t("verlängert sich am", "renews on") : r.c.ends_on < today ? t("endete am", "ended on") : t("endet am", "ends on")} {date(r.c.ends_on, ws.org.timezone)}</small>}
                  </td>
                  <td className="tk-vt-aktion">
                    <div className="tk-vt-knoepfe">
                      {r.c.origin !== "pdf_upload" && <button type="button" onClick={() => vorschau.open(r.c.id)} aria-label={t(`Vorschau: ${r.c.title}`, `Preview: ${r.c.title}`)} title={t("Vorschau", "Preview")} className="tk-vt-vorschau"><Icon name="eye" /><span className="tk-vt-vorschau-text">{t("Vorschau", "Preview")}</span></button>}
                      <Link className={`btn${r.step.kind === "wait" || r.step.kind === "none" || r.step.kind === "edit" ? "" : " primary"}`} to={`/agentur/vertraege/${r.c.id}`}>{SHORT_STEP[r.step.kind]}</Link>
                      <VertragMenue c={r.c} step={r.step.kind} />
                    </div>
                  </td>
                </tr>
  );
  // K-540: Gruppieren nach Creator bzw. Marke (Entscheidung Inhaber 20.10.2026: keine eigenen Ordnerseiten, Reiter bleiben)
  const gruppen = gruppe === "" ? null : (() => {
    const m = new Map<string, { key: string; name: string; ohne: boolean; rows: (typeof sorted)[number][]; entwurf: number; unterschrift: number; fertig: number }>();
    for (const r of sorted) {
      // K-553: über die ID gruppieren (zwei Creator oder Marken mit gleichem Namen bleiben getrennt)
      const id = gruppe === "creator" ? r.c.talent_id : r.c.brand_id;
      const roh = gruppe === "creator" ? r.talent : r.brand;
      const ohne = !id;
      const name = !ohne && roh && roh !== "–" ? roh : ohne ? (gruppe === "creator" ? t("Ohne Creator", "No creator") : t("Ohne Marke", "No brand")) : "–";
      const key = `${gruppe}:${id ?? "ohne"}`;
      const g = m.get(key) ?? { key, name, ohne, rows: [], entwurf: 0, unterschrift: 0, fertig: 0 };
      g.rows.push(r);
      if (r.c.status === "draft") g.entwurf += 1;
      else if (r.c.status === "sent" || r.c.status === "partially_signed") g.unterschrift += 1;
      else if (r.c.status === "signed") g.fertig += 1;
      m.set(key, g);
    }
    // „Ohne Creator“ bzw. „Ohne Marke“ steht am Ende
    return [...m.values()].sort((a, b) => Number(a.ohne) - Number(b.ohne) || a.name.localeCompare(b.name, "de"));
  })();
  return (
    <main id="main" className="content">
      <PageHead title={t("Verträge", "Contracts")} eyebrow={ta("Agentur-Arbeitsbereich", "Agency workspace", "Dein Creator-Konto", "Your creator account")} text={t("Entwerfen, versenden, unterschreiben lassen. Jeder Schritt steht in der Beweiskette.", "Draft, send, have signed. Every step is recorded in the audit trail.")}>
        {/* D-2 (Inhaber 21.10.2026): eine Hauptaktion, Nebenaktionen hinter „Mehr“ */}
        <Link className="btn primary" to="/agentur/vertraege/neu" data-coco="vertrag-erstellen">{t("Vertrag erstellen", "Create contract")}</Link>
        <PdfDesignLink art="contract" />
        <KopfMehr>
          <Link to="/agentur/vertraege/vorlagen">{t("Vorlagen", "Templates")}</Link>
          {!nurLesen && ws.can("sign_contracts") && <button type="button" onClick={() => setExternal(true)}>{t("Extern unterschriebenen Vertrag erfassen", "Record externally signed contract")}</button>}
          {!nurLesen && <Link to="/agentur/vertraege/neu?art=dokument">{t("PDF der Marke unterschreiben lassen", "Have the brand's PDF signed")}</Link>}
          <VoreinstellungenEintrag modul="vertraege" />
        </KopfMehr>
      </PageHead>

      <section className="grid four kpis tk-kacheln" aria-label={t("Kennzahlen", "Metrics")}>
        <div className="kpi">
          <small>{t("Handlung nötig", "Action needed")}</small>
          <div className="metric num">{kpis.declined + kpis.expired + kpis.countersign}</div>
          <small>{kpis.declined} {t("abgelehnt · ", "declined · ")}{kpis.expired} {t("Link abgelaufen · ", "Link expired · ")}{kpis.countersign} {t("gegenzeichnen", "countersign")}</small>
        </div>
        <div className="kpi">
          <small>{t("Zur Unterschrift", "Awaiting signature")}</small>
          <div className="metric num">{kpis.waiting}</div>
          <small>{t("Versendet, noch nicht vollständig", "Sent, not yet complete")}</small>
        </div>
        <div className="kpi">
          <small>{t("Entwürfe", "drafts")}</small>
          <div className="metric num">{kpis.drafts}</div>
          <small>{t("Noch nicht versendet", "Not yet sent")}</small>
        </div>
        <div className="kpi">
          <small>{t("Diesen Monat unterschrieben", "Signed this month")}</small>
          <div className="metric num">{kpis.signedThisMonth}</div>
          <small>{maxPerMonth != null && maxPerMonth >= 0 ? t(`${sentThisMonth} von ${maxPerMonth} Versänden im Tarif genutzt`, `${sentThisMonth} of ${maxPerMonth} dispatches in the plan used`) : t(`${sentThisMonth} diesen Monat versendet`, `${sentThisMonth} sent this month`)}</small>
        </div>
      </section>

      <Toolbar
        search={search}
        onSearch={setSearch}
        label={t("Verträge suchen", "Search contracts")}
        placeholder={ws.isCreator ? t("Vertrag, Nummer oder Marke …", "Contract, number or brand …") : t("Vertrag, Nummer, Creator oder Marke …", "Contract, number, creator or brand …")}
        filters={FILTERS.map((f) => ({ key: f, label: FILTER_LABEL[f], count: f === "alle" || f === "aufgehoben" || f === "abgeschlossen" ? undefined : counts[f] }))}
        filter={filter}
        onFilter={setFilter}
      >
        <label className="sr-only" htmlFor="vt-art">{t("Vertragsart", "Contract type")}</label>
        <select id="vt-art" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">{t("Alle Vertragsarten", "All contract types")}</option>
          {list.data.kinds.filter((k) => !k.archived_at || list.data.contracts.some((c) => c.kind_id === k.id)).map((k) => <option key={k.id} value={k.id}>{nameIn(k.name_de, k.name_en)}{k.archived_at ? t(" (archiviert)", " (archived)") : ""}</option>)}
        </select>
        {/* Studio (Creator-Konto): kein Creator-Filter (das Attribut hidden allein überstimmt die Toolbar-Gestaltung nicht) */}
        {!ws.isCreator && <>
          <label className="sr-only" htmlFor="vt-creator">Creator</label>
          <select id="vt-creator" value={talent} onChange={(e) => setTalent(e.target.value)}>
            <option value="">{t("Alle Creator", "All creators")}</option>
            {(talents.data ?? []).map((t) => <option key={t.id} value={t.id}>{talentName(t)}</option>)}
          </select>
        </>}
        <label className="sr-only" htmlFor="vt-marke">{t("Marke", "Brand")}</label>
        <select id="vt-marke" value={brand} onChange={(e) => setBrand(e.target.value)}>
          <option value="">{t("Alle Marken", "All brands")}</option>
          {(brands.data ?? []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <label className="sr-only" htmlFor="vt-gruppe">{t("Gruppieren", "Group")}</label>
        <select id="vt-gruppe" value={gruppe} onChange={(e) => { setGruppe(e.target.value as "" | "creator" | "marke"); setZugeklappt(new Set()); }}>
          <option value="">{t("Nicht gruppieren", "No grouping")}</option>
          {!ws.isCreator && <option value="creator">{t("Nach Creator gruppieren", "Group by creator")}</option>}
          <option value="marke">{t("Nach Marke gruppieren", "Group by brand")}</option>
        </select>
      </Toolbar>

      {rows.length === 0 ? (
        <EmptyState title={t("Noch keine Verträge.", "No contracts yet.")} text={t("Erstelle den ersten Vertrag aus einer Vorlage oder erfasse einen extern unterschriebenen Vertrag.", "Create the first contract from a template or record an externally signed contract.")} action={<Link className="btn primary" to="/agentur/vertraege/neu">{t("Vertrag erstellen", "Create contract")}</Link>} />
      ) : (
        <>
        <SortWahl />
        <div className="tablewrap tk-vt-liste tk-sticky-end">
          <table>
            <caption className="sr-only">{t("Verträge", "Contracts")}</caption>
            <thead>
              <tr>
                <SortTh label={t("Nr.", "No.")} k="nummer" sort={sort} toggle={toggle} className="tk-vt-c-nr" />
                <SortTh label={t("Vertrag", "Contract")} k="vertrag" sort={sort} toggle={toggle} />
                <SortTh label={ws.isCreator ? t("Marke", "Brand") : t("Creator · Marke", "Creator · brand")} k="creator" sort={sort} toggle={toggle} className="tk-vt-c-parteien" />
                <SortTh label={t("Status und Unterschriften", "Status and signatures")} k="status" sort={sort} toggle={toggle} />
                <SortTh label={t("Versendet · Link bis", "Sent · link until")} k="versendet" sort={sort} toggle={toggle} className="tk-vt-c-versendet" />
                <th scope="col">{t("Nächster Schritt", "Next step")}</th>
              </tr>
            </thead>
            <tbody>
              {/* K-540: ohne Gruppierung die Liste wie bisher; mit Gruppierung je Creator bzw. Marke eine aufklappbare Kopfzeile */}
              {gruppen === null ? sorted.map(zeile) : gruppen.map((g) => {
                const zu = zugeklappt.has(g.key);
                return (
                  <Fragment key={g.key}>
                    <tr className="tk-vt-gruppe">
                      <th scope="rowgroup" colSpan={6}>
                        <button type="button" aria-expanded={!zu} onClick={() => setZugeklappt((z) => { const n = new Set(z); if (n.has(g.key)) n.delete(g.key); else n.add(g.key); return n; })}>
                          <span aria-hidden="true">{zu ? "▸" : "▾"}</span> <strong>{g.name}</strong>{" "}
                          <small>{[t(`${g.rows.length} ${g.rows.length === 1 ? "Vertrag" : "Verträge"}`, `${g.rows.length} ${g.rows.length === 1 ? "contract" : "contracts"}`),
                            g.entwurf ? (g.entwurf === 1 ? t("1 Entwurf", "1 draft") : t(`${g.entwurf} Entwürfe`, `${g.entwurf} drafts`)) : null,
                            g.unterschrift ? t(`${g.unterschrift} zur Unterschrift`, `${g.unterschrift} awaiting signature`) : null,
                            g.fertig ? t(`${g.fertig} abgeschlossen`, `${g.fertig} completed`) : null].filter(Boolean).join(" · ")}</small>
                        </button>
                      </th>
                    </tr>
                    {!zu && g.rows.map(zeile)}
                  </Fragment>
                );
              })}
              {sorted.length === 0 && (
                <tr><td colSpan={6}>{t("Keine passenden Ergebnisse. Passe die Suche oder die Filter an.", "No matching results. Adjust the search or the filters.")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        </>
      )}
      {external && <ExternerVertragDialog onClose={() => setExternal(false)} />}
      <VertragVorschau contractId={vorschau.id} onClose={vorschau.close} />
    </main>
  );
}
